import { useEffect, useState } from "react";
import axios from "@/features/axiosHelper";
import _ from "lodash";
import {
  Box,
  Dialog,
  Paper,
  Typography,
  DialogContent,
  Select,
  MenuItem,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Collapse,
} from "@mui/material";
import { PendingArtifactStatusEnum } from "@/features/connections/integrationSlice";
import { useDispatch } from "react-redux";
import { createAlert } from "@/features/homepage/alertSliceList";
import ShepButton from "@/components/GenericComponents/ShepButton";
import {
  CloseIcon,
  ArrowDownInactiveComponent,
  ArrowLeftInactive,
  ArrowRightInactive,
} from "@/ImageRepo";
import { EditableArtifact } from "./EditableArtifact";
import LoadingBar from "@/components/shared/LoadingBar";

const PendingArtifactEditor = ({
  pendingArtifactId,
  isOpen,
  setIsOpen,
  triggerRefresh,
}) => {
  const [currentInfo, setCurrentInfo] = useState({});
  const [artifactData, setArtifactData] = useState({});
  const [attachmentBlob, setAttachmentBlob] = useState(null);
  const [availableArtifactTypes, setAvailableArtifactTypes] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedArtifactType, setSelectedArtifactType] = useState(-1);
  const [selectedConnection, setSelectedConnection] = useState(-1);
  const [confirmDisabled, setConfirmDisabled] = useState(true);
  const [rejectDisabled, setRejectDisabled] = useState(true);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [saveRequeueDisabled, setSaveRequeueDisabled] = useState(true);
  const [editDisabled, setEditDisabled] = useState(true);
  const [statusTypes, setStatusTypes] = useState([]);
  const [leftColumnWidth, setLeftColumnWidth] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isAttachmentLoaded, setIsAttachmentLoaded] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [viewJSONOpen, setViewJSONOpen] = useState(false);
  const [pendingRawJSON, setPendingRawJSON] = useState("");
  const dispatch = useDispatch();
  const selectedConnectionName =
    connections?.find((c) => c.Key === selectedConnection)?.Label ||
    "Email Connection";

  const onFieldUpdate = (path, value) => {
    const artifactInfoCopy = { ...artifactData };
    _.set(artifactInfoCopy, path, value);
    setArtifactData(artifactInfoCopy);
  };

  const isFormInvalid = () => {
    let errorMessage = "";
    const missingHeaderInfo =
      artifactData?.DocumentHeader?.PurchaseOrderNumber === "" ||
      artifactData.DocumentHeader.CustomerCatalogId === null ||
      artifactData.DocumentHeader.ShipToDetails?.CatalogId === null;

    if (missingHeaderInfo) errorMessage = "Missing required header data.\n";
    if (!artifactData?.DocumentDetails?.length) {
      errorMessage +=
        "Requires at least one Document Detail to confirm document.\n";
    }
    const missingDetails = artifactData.DocumentDetails?.filter(
      (d) =>
        d.Quantity === "" ||
        d.Quantity == null ||
        d.UnitPrice === "" ||
        d.UnitPrice == null ||
        d.ItemCatalogId === null ||
        d.ItemUOMCatalogId === null,
    ).map((x) => x.LineNumber);

    if (missingDetails.length > 0) {
      errorMessage += `Missing required detail data on the following detail line numbers: ${missingDetails.join(", ")}`;
    }
    return errorMessage;
  };

  useEffect(() => {
    getArtifactTypes();
    getConnections();
    getStatusTypes();
    if (pendingArtifactId != null) {
      getPreviewData();
      getAttachmentData();
    }
  }, [pendingArtifactId]);

  const populateRowIds = (array, type) => {
    if (!array || !Array.isArray(array)) return array;

    return array.map((row, index) => {
      //always assign new unique TempId
      if (!row.TempId) {
        return { ...row, TempId: `${type}-${Date.now()}-${index}` };
      }
      return row;
    });
  };

  useEffect(() => {
    if (currentInfo.ArtifactDataJSON != null) {
      const data = JSON.parse(currentInfo.ArtifactDataJSON);
      if (data.DocumentHeader) {
        data.DocumentHeader.Taxes = populateRowIds(
          data.DocumentHeader.Taxes,
          "TAX",
          "TaxCode",
        );
        data.DocumentHeader.MiscAllowanceCharges = populateRowIds(
          data.DocumentHeader.MiscAllowanceCharges,
          "ALLOWCHAR",
          "ACCode",
        );
        data.DocumentHeader.OptionalFields = populateRowIds(
          data.DocumentHeader.OptionalFields,
          "OPTFIELD",
          "FieldCode",
        );
      }
      if (data.DocumentDetails) {
        data.DocumentDetails = data.DocumentDetails.map((detail) => ({
          ...detail,
          Taxes: populateRowIds(detail.Taxes, "DTL-TAX", "TaxCode"),
          MiscAllowanceCharges: populateRowIds(
            detail.MiscAllowanceCharges,
            "DTL-ALLOWCHAR",
            "ACCode",
          ),
          OptionalFields: populateRowIds(
            detail.OptionalFields,
            "DTL-OPTFIELD",
            "FieldCode",
          ),
        }));
      }
      setArtifactData(data);
    }
  }, [currentInfo]);

  useEffect(() => {
    setAvailableActions();
  }, [currentInfo, selectedArtifactType, selectedConnection]);

  //for changing the ratio between source and target column
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) {
        return;
      }
      let newColumnWidth = (e.clientX / window.innerWidth) * 100;
      if (newColumnWidth > 20 && newColumnWidth < 80) {
        setLeftColumnWidth(newColumnWidth);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    //dragging near iframe causes problems (disable its ability to look for mouse events)
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "none";
    } else {
      document.body.style.userSelect = "";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
    };
  }, [isDragging]);

  const saveChanges = async () => {
    let success = false;
    const pendingChanges = {
      ArtifactDataJson: JSON.stringify(artifactData),
      PendingArtifactId: pendingArtifactId,
      ConnectionId: selectedConnection,
      ArtifactType: selectedArtifactType,
    };
    await axios
      .post("/email/save", pendingChanges, null)
      .then((response) => {
        dispatch(
          createAlert({
            status: "Success",
            alertType: "single",
            message: response.data,
          }),
        );
        success = true;
        setPendingRawJSON(JSON.stringify(artifactData, null, 2));
      })
      .catch(async (error) => {
        console.error("Error saving data: ", error);
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          }),
        );
      });
    return success;
  };
  const confirmArtifact = async () => {
    const errorMessage = isFormInvalid();
    if (errorMessage !== "") {
      setShowValidationErrors(true);
      dispatch(
        createAlert({
          status: "Error",
          alertType: "result",
          title: "Validation Error",
          message: errorMessage,
          buttonLabel: "Close",
        }),
      );
      return;
    }
    setShowValidationErrors(false);
    const saved = await saveChanges();
    if (saved) {
      await axios
        .post("/email", pendingArtifactId, null)
        .then((response) => {
          dispatch(
            createAlert({
              status: "Success",
              alertType: "single",
              message: response.data,
            }),
          );
          triggerRefresh();
          setIsOpen(false);
        })
        .catch(async (error) => {
          console.error("Error confirming data: ", error);
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              message: error.response.data,
              buttonLabel: "Close",
            }),
          );
        });
    }
  };
  const rejectPendingArtifact = async () => {
    await axios
      .post("/email/Reject", null, { params: { pendingArtifactId } })
      .then((response) => {
        dispatch(
          createAlert({
            status: "Success",
            alertType: "single",
            message: response.data,
          }),
        );
        triggerRefresh();
        setIsOpen(false);
      })
      .catch(async (error) => {
        console.error("Error rejecting data: ", error);
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          }),
        );
      });
  };

  const saveAndRequeue = async () => {
    const errorMessage = isFormInvalid();
    if (errorMessage !== "") {
      setShowValidationErrors(true);
      dispatch(
        createAlert({
          status: "Error",
          alertType: "result",
          title: "Validation Error",
          message: errorMessage,
          buttonLabel: "Close",
        }),
      );
      return;
    }
    setShowValidationErrors(false);
    const pendingChanges = {
      PendingArtifactId: pendingArtifactId,
      ConnectionId: selectedConnection,
      ArtifactType: selectedArtifactType,
    };
    await axios
      .post("/email/save-requeue", pendingChanges, null)
      .then((response) => {
        dispatch(
          createAlert({
            status: "Success",
            alertType: "single",
            message: response.data,
          }),
        );
        triggerRefresh();
        setIsOpen(false);
      })
      .catch(async (error) => {
        console.error("Error saving and requeuing data: ", error);
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          }),
        );
      });
  };

  const setAvailableActions = () => {
    if (!currentInfo || Object.keys(currentInfo).length === 0) {
      setConfirmDisabled(true);
      setEditDisabled(true);
      setRejectDisabled(true);
      setSaveDisabled(true);
      setSaveRequeueDisabled(true);
      return;
    }

    if (
      selectedConnection != currentInfo.ConnectionId ||
      selectedArtifactType != currentInfo.ArtifactType
    ) {
      //set confirm / reject status
      setConfirmDisabled(true);
      setEditDisabled(true);
      if (
        currentInfo.Status === PendingArtifactStatusEnum.Confirmed ||
        currentInfo.Status === PendingArtifactStatusEnum.Completed ||
        currentInfo.Status === PendingArtifactStatusEnum.Rejected
      ) {
        setRejectDisabled(true);
      } else {
        setRejectDisabled(false);
      }

      //handle save/save and requeue logic
      if (currentInfo.Status === PendingArtifactStatusEnum.New) {
        //only allowed if connection / artifact type is unknown
        setSaveDisabled(true);
        if (
          (currentInfo.ConnectionId ?? 0) <= 0 ||
          (currentInfo.ArtifactType ?? 0) <= 0
        ) {
          setSaveRequeueDisabled(false);
        } else {
          setSaveRequeueDisabled(true);
        }
      } else if (
        currentInfo.Status === PendingArtifactStatusEnum.ErrorInTranslation ||
        currentInfo.Status === PendingArtifactStatusEnum.Rejected ||
        currentInfo.Status === PendingArtifactStatusEnum.AwaitingConfirmation ||
        currentInfo.Status === PendingArtifactStatusEnum.Confirmed ||
        currentInfo.Status === PendingArtifactStatusEnum.Completed
      ) {
        setSaveDisabled(true);
        setSaveRequeueDisabled(false);
      }
    } else {
      //unchanged
      //handle confirm/reject logic
      if (
        currentInfo.Status === PendingArtifactStatusEnum.AwaitingConfirmation
      ) {
        setConfirmDisabled(false);
        setRejectDisabled(false);
      } else {
        setConfirmDisabled(true);
        if (
          currentInfo.Status === PendingArtifactStatusEnum.Confirmed ||
          currentInfo.Status === PendingArtifactStatusEnum.Completed ||
          currentInfo.Status === PendingArtifactStatusEnum.Rejected
        ) {
          setRejectDisabled(true);
        } else {
          setRejectDisabled(false);
        }
      }

      //handle save/save & requeue logic
      if (
        currentInfo.Status === PendingArtifactStatusEnum.New ||
        currentInfo.Status === PendingArtifactStatusEnum.ErrorInTranslation
      ) {
        //do not allow
        setSaveDisabled(true);
        setSaveRequeueDisabled(true);
        setEditDisabled(true);
      } else if (
        currentInfo.Status === PendingArtifactStatusEnum.AwaitingConfirmation ||
        currentInfo.Status === PendingArtifactStatusEnum.Rejected
      ) {
        //allow save
        setSaveDisabled(false);
        setSaveRequeueDisabled(true);
        setEditDisabled(false);
      } else {
        //confirmed / completed
        //allowed to save, overwrite data in already created artifact
        setSaveDisabled(false);
        setSaveRequeueDisabled(true);
        setConfirmDisabled(false);
        setEditDisabled(false);
      }
    }
  };

  const getPreviewData = () => {
    if (pendingArtifactId !== "") {
      setIsDataLoaded(false);
      axios
        .get("/email/preview-data", {
          params: { pendingArtifactId },
        })
        .then((response) => {
          if (response != null) {
            setCurrentInfo(response.data);
            setSelectedArtifactType(response.data.ArtifactType);
            setSelectedConnection(response.data.ConnectionId);
            setPendingRawJSON(response.data.ArtifactDataJSON);
          }
          setTimeout(() => {
            setIsDataLoaded(true);
          }, 100);
        })
        .catch((error) => {
          console.error("Error fetching raw file data: ", error);
          setIsDataLoaded(true);
        });
    }
  };
  const getAttachmentData = async () => {
    if (pendingArtifactId !== "") {
      setIsAttachmentLoaded(false);
      const response = await axios.get("/email/attachment-data", {
        params: { pendingArtifactId },
        responseType: "blob",
      });
      // Create a URL for the blob data
      if (response.data.size == 0) {
        setAttachmentBlob(null);
        setIsAttachmentLoaded(true);
        return;
      }
      const url = URL.createObjectURL(response.data);
      setAttachmentBlob(url);
      setIsAttachmentLoaded(true);
    }
  };
  const getArtifactTypes = async () => {
    const response = await axios.get("/email/artifact-types");
    setAvailableArtifactTypes(response.data);
  };
  const getConnections = async () => {
    const response = await axios.get("/email/connections");
    setConnections(response.data);
  };
  const getStatusTypes = () => {
    axios
      .get("/email/email-status")
      .then((response) => {
        setStatusTypes(response.data);
      })
      .catch((error) => {
        console.error("Error fetching inbound status: ", error);
      });
  };
  const getStatusName = (pendingStatus) => {
    const matchingVal = statusTypes.find(
      (item) => item.Value === pendingStatus,
    );

    return matchingVal ? matchingVal.Label : "";
  };

  const universalHeaderHeight = "85px";

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        fullScreen
        PaperProps={{
          style: {
            marginTop: universalHeaderHeight,
            backgroundColor: "var(--disabled)",
            boxShadow: "none",
            height: `calc(100vh - ${universalHeaderHeight})`,
            overflow: "hidden",
            borderRadius: "15px 15px 0 0",
          },
        }}
      >
        <Box
          sx={{
            px: 5,
            pt: 2,
            pb: 2,
            backgroundColor: "var(--default-light)",
            position: "relative",
            display: "flex",
            gap: "40px",
          }}
        >
          <FormControlLabel
            label="Document Type"
            labelPlacement="top"
            control={
              <Select
                size="small"
                onChange={(e) => setSelectedArtifactType(e.target.value)}
                value={selectedArtifactType}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value={-1}>Please select</MenuItem>
                {availableArtifactTypes?.map((data) => (
                  <MenuItem value={data.Key}>{data.Label}</MenuItem>
                ))}
              </Select>
            }
          />
          <FormControlLabel
            label="Connection"
            labelPlacement="top"
            control={
              <Select
                size="small"
                onChange={(e) => setSelectedConnection(e.target.value)}
                value={selectedConnection}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value={-1}>Please select</MenuItem>
                {connections?.map((data) => (
                  <MenuItem value={data.Key}>{data.Label}</MenuItem>
                ))}
              </Select>
            }
          />
          <Typography
            variant="h2"
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              fontWeight: "var(--font-bold)",
              color: "var(--primary-main)",
              fontSize: "30px !important",
            }}
          >
            Edit Document
          </Typography>
          <IconButton
            onClick={() => setIsOpen(false)}
            sx={{
              position: "absolute",
              top: "26px",
              right: "24px",
              color: "var(--primary-main)",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "row",
            paddingTop: "0 !important",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              width: `${leftColumnWidth}%`,
              p: 3,
              pr: 1,
            }}
          >
            {/* LEFT SIDE */}
            <Paper
              sx={{
                flex: "1",
                overflowY: "auto",
                p: 2,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  mt: 1,
                  mb: 1,
                  fontWeight: "var(--font-bold)",
                  textAlign: "center",
                }}
              >
                Source
              </Typography>
              <Accordion defaultExpanded sx={{ margin: "15px" }}>
                <AccordionSummary expandIcon={<ArrowDownInactiveComponent />}>
                  <Typography variant="h3">Email Body</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {!isDataLoaded ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        pb: "80px",
                      }}
                    >
                      <LoadingBar isDataLoaded={false} secondsToStall={1} />
                    </Box>
                  ) : (
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "break-word",
                      }}
                    >
                      <p>{currentInfo.EmailBody ?? "No content in body."}</p>
                    </div>
                  )}
                </AccordionDetails>
              </Accordion>
              <Accordion defaultExpanded sx={{ margin: "15px" }}>
                <AccordionSummary expandIcon={<ArrowDownInactiveComponent />}>
                  <Typography variant="h3">Attachment</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {!isDataLoaded || !isAttachmentLoaded ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <LoadingBar isDataLoaded={false} secondsToStall={1} />
                      <Box sx={{ height: "80px" }} />
                    </Box>
                  ) : attachmentBlob != null ? (
                    <iframe
                      id="pdf-viewer"
                      title="PDF Document"
                      width="100%"
                      height="800px"
                      src={attachmentBlob}
                      style={{
                        border: "none",
                        pointerEvents: isDragging ? "none" : "auto",
                      }}
                    >
                      <p>
                        Your browser does not support iframes. You can{" "}
                        <a id="pdf-download-link">download the PDF</a> instead.
                      </p>
                    </iframe>
                  ) : (
                    <Typography>No attachment data</Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Paper>
          </Box>

          {/* DIVIDING DRAGGER */}
          <Box
            onMouseDown={() => setIsDragging(true)}
            sx={{
              width: "8px",
              cursor: "col-resize",
              backgroundColor: isDragging
                ? "var(--primary-main)"
                : "var(--border-primary)",
              "&:hover": { backgroundColor: "var(--primary-main)" },
              zIndex: 10,
              marginTop: "30px",
              marginBottom: "-14px",
            }}
          ></Box>

          {/* RIGHT SIDE */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "10px",
              flex: 1,
              height: "100%",
              overflow: "hidden",
              p: 3,
              pl: 1,
            }}
          >
            <Paper
              sx={{
                flex: "1",
                overflowY: "auto",
                p: 2,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 1,
                }}
              >
                <Typography
                  variant="subheading"
                  sx={{
                    position: "absolute",
                    left: 5,
                  }}
                >
                  {currentInfo?.Confidence ?? 0}% Confidence
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "var(--font-bold)",
                  }}
                >
                  Target
                </Typography>
                <Typography
                  variant="subheading"
                  sx={{
                    position: "absolute",
                    right: 5,
                  }}
                >
                  {getStatusName(currentInfo.Status)}
                </Typography>
              </Box>

              <EditableArtifact
                artifactData={artifactData}
                onChange={(a, b) => onFieldUpdate(a, b)}
                isDataLoaded={isDataLoaded}
                connectionId={selectedConnection}
                connectionName={selectedConnectionName}
                showValidationErrors={showValidationErrors}
                isDisabled={editDisabled}
              />
            </Paper>
            <Paper
              sx={{
                //flex: 1,
                padding: "10px",
                overflowY: "hidden",
                p: 2,
              }}
            >
              <IconButton onClick={() => setViewJSONOpen(!viewJSONOpen)}>
                {!viewJSONOpen ? <ArrowLeftInactive /> : <ArrowRightInactive />}
              </IconButton>
              <Collapse
                orientation="horizontal"
                in={viewJSONOpen}
                unmountOnExit
                sx={{ height: "100%" }}
              >
                <Box
                  sx={{ width: "500px", height: "100%", overflowY: "auto" }}
                  style={{
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                  }}
                >
                  {pendingRawJSON ?? "No document information to display."}
                </Box>
              </Collapse>
            </Paper>
          </Box>
        </DialogContent>

        {/* FOOTER BUTTONS */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 1,
            px: 7,
            py: 2,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "15px",
          }}
        >
          <ShepButton
            className="btn-primary"
            onClick={saveChanges}
            disabled={saveDisabled}
          >
            Save Changes
          </ShepButton>
          <ShepButton
            className="btn-primary"
            onClick={saveAndRequeue}
            disabled={saveRequeueDisabled}
          >
            Save & Requeue
          </ShepButton>
          <ShepButton
            className="btn-primary"
            onClick={confirmArtifact}
            disabled={confirmDisabled}
          >
            Save & Confirm
          </ShepButton>
          <ShepButton
            className="btn-primary"
            onClick={rejectPendingArtifact}
            disabled={rejectDisabled}
          >
            Reject
          </ShepButton>
        </Box>
      </Dialog>
    </>
  );
};
export default PendingArtifactEditor;
